"use client";

import { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faCheckCircle,
  faCircleExclamation,
  faCreditCard,
  faLock,
  faSpinner,
  faXmark
} from "@fortawesome/free-solid-svg-icons";
import { showToast } from "@/components/ui/Toast";
import { createCardPayment, getMercadoPagoPublicKey, type CardPaymentResponse } from "@/lib/payments";
import { apiFetch } from "@/lib/api";

declare global {
  interface Window {
    MercadoPago: any;
  }
}

interface CardPaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (response: CardPaymentResponse) => void;
  onBack?: () => void;
  couponCode?: string;
  target: {
    kind: "plan" | "one_time";
    planCode?: string;
    productId?: string;
    productTitle?: string;
    planName: string;
    amount: number;
    currency?: string;
  } | null;
}

interface CardFormData {
  cardNumber: string;
  cardholderName: string;
  expirationMonth: string;
  expirationYear: string;
  securityCode: string;
  identificationType: string;
  identificationNumber: string;
  email: string;
  installments: number;
}

const formatCurrency = (value: number, currency: string = "BRL") => {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency
  }).format(value);
};

const STATUS_MESSAGES: Record<string, { title: string; description: string; type: "success" | "error" | "warning" }> = {
  approved: { title: "Pagamento aprovado!", description: "Seu pagamento foi processado com sucesso.", type: "success" },
  pending: { title: "Pagamento pendente", description: "Seu pagamento está sendo processado.", type: "warning" },
  in_process: { title: "Processando pagamento", description: "Aguarde enquanto confirmamos seu pagamento.", type: "warning" },
  rejected: { title: "Pagamento recusado", description: "Seu pagamento foi recusado. Verifique os dados do cartão.", type: "error" },
  cc_rejected_bad_filled_card_number: { title: "Número do cartão inválido", description: "Verifique o número do cartão.", type: "error" },
  cc_rejected_bad_filled_date: { title: "Data de validade inválida", description: "Verifique a data de validade do cartão.", type: "error" },
  cc_rejected_bad_filled_security_code: { title: "Código de segurança inválido", description: "Verifique o código de segurança (CVV).", type: "error" },
  cc_rejected_insufficient_amount: { title: "Saldo insuficiente", description: "O cartão não possui saldo suficiente.", type: "error" },
  cc_rejected_call_for_authorize: { title: "Autorização necessária", description: "Entre em contato com sua operadora para autorizar.", type: "error" }
};

export function CardPaymentModal({ isOpen, onClose, onSuccess, onBack, target, couponCode: initialCouponCode }: CardPaymentModalProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [paymentResult, setPaymentResult] = useState<CardPaymentResponse | null>(null);
  const [mpPublicKey, setMpPublicKey] = useState<string | null>(null);

  // Coupon state
  const [couponCode, setCouponCode] = useState(initialCouponCode ?? "");
  const [couponValidating, setCouponValidating] = useState(false);
  const [couponResult, setCouponResult] = useState<{
    valid: boolean;
    couponCode?: string;
    couponName?: string;
    discountAmount?: number;
    originalAmount?: number;
    finalAmount?: number;
    reason?: string;
  } | null>(null);

  const effectiveAmount = couponResult?.valid && couponResult.finalAmount != null
    ? couponResult.finalAmount
    : target?.amount ?? 0;
  
  const [formData, setFormData] = useState<CardFormData>({
    cardNumber: "",
    cardholderName: "",
    expirationMonth: "",
    expirationYear: "",
    securityCode: "",
    identificationType: "CPF",
    identificationNumber: "",
    email: "",
    installments: 1
  });

  const [paymentMethodId, setPaymentMethodId] = useState<string>("");
  const [issuerId, setIssuerId] = useState<string>("");
  const [installmentOptions, setInstallmentOptions] = useState<{ installments: number; label: string }[]>([]);

  const mpRef = useRef<any>(null);
  const cardFormRef = useRef<any>(null);
  const sdkLoadedRef = useRef(false);

  // Load MercadoPago SDK
  const loadMercadoPagoSDK = useCallback(async () => {
    if (sdkLoadedRef.current && window.MercadoPago) {
      return;
    }

    return new Promise<void>((resolve, reject) => {
      if (window.MercadoPago) {
        sdkLoadedRef.current = true;
        resolve();
        return;
      }

      const script = document.createElement("script");
      script.src = "https://sdk.mercadopago.com/js/v2";
      script.async = true;
      script.onload = () => {
        sdkLoadedRef.current = true;
        resolve();
      };
      script.onerror = () => reject(new Error("Falha ao carregar SDK do MercadoPago"));
      document.body.appendChild(script);
    });
  }, []);

  // Initialize MercadoPago
  useEffect(() => {
    if (!isOpen || !target) return;

    const init = async () => {
      setIsLoading(true);
      setErrorMessage(null);
      setPaymentResult(null);

      try {
        // Get public key from backend
        const { publicKey } = await getMercadoPagoPublicKey();
        setMpPublicKey(publicKey);

        // Load SDK
        await loadMercadoPagoSDK();

        // Initialize MP instance
        if (window.MercadoPago && publicKey) {
          mpRef.current = new window.MercadoPago(publicKey, {
            locale: "pt-BR"
          });
        }
      } catch (error) {
        console.error("Error initializing MercadoPago:", error);
        setErrorMessage("Erro ao inicializar pagamento. Tente novamente.");
      } finally {
        setIsLoading(false);
      }
    };

    init();

    return () => {
      // Cleanup
      if (cardFormRef.current?.unmount) {
        cardFormRef.current.unmount();
      }
    };
  }, [isOpen, target, loadMercadoPagoSDK]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setFormData({
        cardNumber: "",
        cardholderName: "",
        expirationMonth: "",
        expirationYear: "",
        securityCode: "",
        identificationType: "CPF",
        identificationNumber: "",
        email: "",
        installments: 1
      });
      setPaymentMethodId("");
      setIssuerId("");
      setInstallmentOptions([]);
      setErrorMessage(null);
      setPaymentResult(null);
      setCouponCode(initialCouponCode ?? "");
      setCouponResult(null);
      setCouponValidating(false);
    }
  }, [isOpen, initialCouponCode]);

  // Validate coupon
  const validateCoupon = async () => {
    if (!couponCode.trim() || !target || target.kind !== "plan") return;
    setCouponValidating(true);
    setCouponResult(null);
    try {
      const res = await apiFetch<any>("/coupons/validate", {
        method: "POST",
        body: JSON.stringify({
          code: couponCode.trim(),
          planCode: target.planCode,
          planAmount: target.amount,
          paymentMethod: "card"
        })
      });
      setCouponResult(res);
    } catch (err: any) {
      setCouponResult({ valid: false, reason: err.message || "Erro ao validar cupom" });
    } finally {
      setCouponValidating(false);
    }
  };

  // Detect card type from number
  const detectCardType = useCallback(async (cardNumber: string) => {
    if (!mpRef.current || cardNumber.length < 6) return;

    const bin = cardNumber.replace(/\s/g, "").substring(0, 6);
    
    try {
      const paymentMethods = await mpRef.current.getPaymentMethods({ bin });
      if (paymentMethods.results?.length > 0) {
        const method = paymentMethods.results[0];
        setPaymentMethodId(method.id);
        setIssuerId(method.issuer?.id || "");
        
        // Get installments
        if (target?.amount) {
          const installments = await mpRef.current.getInstallments({
            amount: String(target.amount),
            bin,
            paymentTypeId: "credit_card"
          });
          
          if (installments?.length > 0 && installments[0].payer_costs) {
            const options = installments[0].payer_costs.map((cost: any) => ({
              installments: cost.installments,
              label: `${cost.installments}x de ${formatCurrency(cost.installment_amount)} (${cost.recommended_message || ""})`
            }));
            setInstallmentOptions(options);
          }
        }
      }
    } catch (error) {
      console.error("Error detecting card type:", error);
    }
  }, [target?.amount]);

  const handleInputChange = (field: keyof CardFormData, value: string) => {
    let formattedValue = value;

    // Format card number with spaces
    if (field === "cardNumber") {
      formattedValue = value.replace(/\D/g, "").replace(/(\d{4})/g, "$1 ").trim().substring(0, 19);
      
      // Detect card type when we have enough digits
      if (formattedValue.replace(/\s/g, "").length >= 6) {
        detectCardType(formattedValue);
      }
    }

    // Format CPF
    if (field === "identificationNumber") {
      formattedValue = value.replace(/\D/g, "").substring(0, 11);
      if (formattedValue.length > 9) {
        formattedValue = formattedValue.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
      } else if (formattedValue.length > 6) {
        formattedValue = formattedValue.replace(/(\d{3})(\d{3})(\d+)/, "$1.$2.$3");
      } else if (formattedValue.length > 3) {
        formattedValue = formattedValue.replace(/(\d{3})(\d+)/, "$1.$2");
      }
    }

    // Format security code
    if (field === "securityCode") {
      formattedValue = value.replace(/\D/g, "").substring(0, 4);
    }

    // Format month
    if (field === "expirationMonth") {
      formattedValue = value.replace(/\D/g, "").substring(0, 2);
      if (parseInt(formattedValue) > 12) formattedValue = "12";
    }

    // Format year
    if (field === "expirationYear") {
      formattedValue = value.replace(/\D/g, "").substring(0, 2);
    }

    setFormData((prev) => ({ ...prev, [field]: formattedValue }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mpRef.current || !target) return;

    setIsProcessing(true);
    setErrorMessage(null);

    try {
      // Create card token
      const cardData = {
        cardNumber: formData.cardNumber.replace(/\s/g, ""),
        cardholderName: formData.cardholderName,
        cardExpirationMonth: formData.expirationMonth,
        cardExpirationYear: `20${formData.expirationYear}`,
        securityCode: formData.securityCode,
        identificationType: formData.identificationType,
        identificationNumber: formData.identificationNumber.replace(/\D/g, "")
      };

      const tokenResponse = await mpRef.current.createCardToken(cardData);
      
      if (tokenResponse.error) {
        throw new Error(tokenResponse.error);
      }

      // Send payment to backend
      const paymentPayload = {
        intentType: target.kind === "plan" ? "subscription" as const : "one_time" as const,
        planCode: target.kind === "plan" ? target.planCode : undefined,
        productId: target.kind === "one_time" ? target.productId : undefined,
        productTitle: target.kind === "one_time" ? target.productTitle : undefined,
        token: tokenResponse.id,
        paymentMethodId: paymentMethodId || "visa",
        installments: formData.installments,
        issuerId: issuerId || undefined,
        payer: {
          email: formData.email,
          identification: {
            type: formData.identificationType,
            number: formData.identificationNumber.replace(/\D/g, "")
          }
        },
        ...(couponResult?.valid && couponCode ? { couponCode } : {})
      };

      const response = await createCardPayment(paymentPayload);
      setPaymentResult(response);

      if (response.status === "approved") {
        showToast("Pagamento aprovado!", "success");
        onSuccess?.(response);
      } else if (response.status === "rejected") {
        const statusInfo = STATUS_MESSAGES[response.statusDetail] || STATUS_MESSAGES.rejected;
        setErrorMessage(statusInfo.description);
      } else {
        showToast("Pagamento em processamento", "info");
      }
    } catch (error: any) {
      console.error("Payment error:", error);
      const message = error?.message || error?.cause?.message || "Erro ao processar pagamento";
      setErrorMessage(message);
      showToast(message, "error");
    } finally {
      setIsProcessing(false);
    }
  };

  if (!isOpen) return null;

  const content = (
    <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/80 backdrop-blur-sm">
      <div className="relative mx-4 w-full max-w-lg overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-b from-[#1a0a2e] to-[#0f0518] shadow-2xl">
        {/* Header */}
        <header className="flex items-center justify-between border-b border-white/10 px-6 py-4">
          <div className="flex items-center gap-3">
            {onBack && (
              <button
                type="button"
                onClick={onBack}
                className="rounded-full p-2 text-white/60 transition hover:bg-white/10 hover:text-white"
              >
                <FontAwesomeIcon icon={faArrowLeft} className="h-4 w-4" />
              </button>
            )}
            <div>
              <h2 className="text-lg font-semibold text-white">Pagamento com Cartão</h2>
              <p className="text-xs text-white/60">
                {target?.planName} - {couponResult?.valid
                  ? <><span className="line-through text-white/40">{formatCurrency(target?.amount ?? 0, target?.currency)}</span>{" "}<span className="text-green-400 font-semibold">{formatCurrency(effectiveAmount, target?.currency)}</span></>
                  : formatCurrency(target?.amount ?? 0, target?.currency)
                }
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-2 text-white/60 transition hover:bg-white/10 hover:text-white"
          >
            <FontAwesomeIcon icon={faXmark} className="h-5 w-5" />
          </button>
        </header>

        {/* Content */}
        <div className="max-h-[70vh] overflow-y-auto p-6">
          {isLoading ? (
            <div className="flex flex-col items-center gap-4 py-12">
              <FontAwesomeIcon icon={faSpinner} className="h-8 w-8 animate-spin text-brand-secondary" />
              <p className="text-sm text-white/60">Carregando formulário de pagamento...</p>
            </div>
          ) : paymentResult?.status === "approved" ? (
            <div className="flex flex-col items-center gap-4 py-8 text-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500/20">
                <FontAwesomeIcon icon={faCheckCircle} className="h-8 w-8 text-emerald-400" />
              </div>
              <h3 className="text-xl font-semibold text-white">Pagamento Aprovado!</h3>
              <p className="text-sm text-white/60">
                Seu pagamento foi processado com sucesso.
                {paymentResult.lastFourDigits && (
                  <span className="block mt-1">
                    Cartão final: **** {paymentResult.lastFourDigits}
                  </span>
                )}
              </p>
              <button
                type="button"
                onClick={onClose}
                className="mt-4 rounded-full bg-brand-gradient px-6 py-3 text-sm font-semibold text-white"
              >
                Fechar
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {errorMessage && (
                <div className="rounded-xl border border-red-500/40 bg-red-500/15 px-4 py-3 text-sm text-red-200">
                  <FontAwesomeIcon icon={faCircleExclamation} className="mr-2 h-4 w-4" />
                  {errorMessage}
                </div>
              )}

              {/* Coupon Input — only for professional plan */}
              {target?.kind === "plan" && target?.planCode === "professional" && (
                <div className="rounded-xl border border-white/10 bg-white/5 p-3">
                  <label className="mb-1.5 block text-xs font-medium text-white/50">
                    Cupom de desconto
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={couponCode}
                      onChange={(e) => {
                        setCouponCode(e.target.value.toUpperCase());
                        if (couponResult) setCouponResult(null);
                      }}
                      placeholder="Digite seu cupom"
                      className="flex-1 rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-sm font-mono text-white placeholder-white/30 focus:border-brand-secondary focus:outline-none"
                    />
                    <button
                      type="button"
                      onClick={validateCoupon}
                      disabled={!couponCode.trim() || couponValidating}
                      className="inline-flex items-center gap-2 rounded-lg bg-brand-secondary/20 border border-brand-secondary/40 px-3 py-2 text-sm font-medium text-brand-secondary transition hover:bg-brand-secondary/30 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {couponValidating ? (
                        <FontAwesomeIcon icon={faSpinner} spin className="h-3.5 w-3.5" />
                      ) : (
                        "Aplicar"
                      )}
                    </button>
                  </div>
                  {couponResult && (
                    <div className={clsx(
                      "mt-2 rounded-lg px-3 py-2 text-xs",
                      couponResult.valid
                        ? "border border-green-500/30 bg-green-500/10 text-green-400"
                        : "border border-red-500/30 bg-red-500/10 text-red-400"
                    )}>
                      {couponResult.valid ? (
                        <>
                          <FontAwesomeIcon icon={faCheckCircle} className="mr-1.5 h-3 w-3" />
                          Cupom <strong>{couponResult.couponCode}</strong> aplicado!
                          {" "}Desconto de {formatCurrency(couponResult.discountAmount ?? 0, target?.currency ?? "BRL")}
                          {" "}— Novo valor: <strong>{formatCurrency(couponResult.finalAmount ?? 0, target?.currency ?? "BRL")}</strong>
                        </>
                      ) : (
                        <>
                          <FontAwesomeIcon icon={faCircleExclamation} className="mr-1.5 h-3 w-3" />
                          {couponResult.reason}
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Card Number */}
              <div>
                <label className="mb-1.5 block text-xs font-medium text-white/70">
                  Número do Cartão
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={formData.cardNumber}
                    onChange={(e) => handleInputChange("cardNumber", e.target.value)}
                    placeholder="0000 0000 0000 0000"
                    className="w-full rounded-xl border border-white/15 bg-white/5 px-4 py-3 pl-11 text-white placeholder:text-white/40 focus:border-brand-secondary/50 focus:outline-none"
                    required
                  />
                  <FontAwesomeIcon 
                    icon={faCreditCard} 
                    className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40" 
                  />
                </div>
              </div>

              {/* Cardholder Name */}
              <div>
                <label className="mb-1.5 block text-xs font-medium text-white/70">
                  Nome no Cartão
                </label>
                <input
                  type="text"
                  value={formData.cardholderName}
                  onChange={(e) => handleInputChange("cardholderName", e.target.value.toUpperCase())}
                  placeholder="NOME COMPLETO"
                  className="w-full rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-white placeholder:text-white/40 focus:border-brand-secondary/50 focus:outline-none"
                  required
                />

              </div>

              {/* Expiration & CVV */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-white/70">Mês</label>
                  <input
                    type="text"
                    value={formData.expirationMonth}
                    onChange={(e) => handleInputChange("expirationMonth", e.target.value)}
                    placeholder="MM"
                    className="w-full rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-center text-white placeholder:text-white/40 focus:border-brand-secondary/50 focus:outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-white/70">Ano</label>
                  <input
                    type="text"
                    value={formData.expirationYear}
                    onChange={(e) => handleInputChange("expirationYear", e.target.value)}
                    placeholder="AA"
                    className="w-full rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-center text-white placeholder:text-white/40 focus:border-brand-secondary/50 focus:outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-white/70">CVV</label>
                  <input
                    type="text"
                    value={formData.securityCode}
                    onChange={(e) => handleInputChange("securityCode", e.target.value)}
                    placeholder="123"
                    className="w-full rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-center text-white placeholder:text-white/40 focus:border-brand-secondary/50 focus:outline-none"
                    required
                  />
                </div>
              </div>

              {/* CPF */}
              <div>
                <label className="mb-1.5 block text-xs font-medium text-white/70">CPF</label>
                <input
                  type="text"
                  value={formData.identificationNumber}
                  onChange={(e) => handleInputChange("identificationNumber", e.target.value)}
                  placeholder="000.000.000-00"
                  className="w-full rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-white placeholder:text-white/40 focus:border-brand-secondary/50 focus:outline-none"
                  required
                />
              </div>

              {/* Email */}
              <div>
                <label className="mb-1.5 block text-xs font-medium text-white/70">E-mail</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                  placeholder="seu@email.com"
                  className="w-full rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-white placeholder:text-white/40 focus:border-brand-secondary/50 focus:outline-none"
                  required
                />
              </div>

              {/* Installments */}
              {installmentOptions.length > 0 && (
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-white/70">Parcelas</label>
                  <select
                    value={formData.installments}
                    onChange={(e) => setFormData((prev) => ({ ...prev, installments: parseInt(e.target.value) }))}
                    className="w-full rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-white focus:border-brand-secondary/50 focus:outline-none"
                  >
                    {installmentOptions.map((option) => (
                      <option key={option.installments} value={option.installments} className="bg-[#1a0a2e]">
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isProcessing || !formData.cardNumber || !formData.cardholderName}
                className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-brand-gradient px-6 py-4 text-sm font-semibold text-white shadow-glow transition hover:scale-[1.01] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isProcessing ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} className="h-4 w-4 animate-spin" />
                    Processando...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faLock} className="h-4 w-4" />
                    Pagar {formatCurrency(effectiveAmount, target?.currency)}
                  </>
                )}
              </button>

              {/* Security Notice */}
              <p className="text-center text-xs text-white/40">
                <FontAwesomeIcon icon={faLock} className="mr-1 h-3 w-3" />
                Pagamento seguro processado pelo Mercado Pago
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
}
